/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import clsx from "clsx"
import { observer } from "mobx-react-lite"
import type { SubmitEvent } from "react"
import { useCallback, useEffect, useId, useState } from "react"

import { canManageRole } from "../../features/access-rights/access-rights"
import { ApplicationTypeViewModel } from "../../features/application-type/application-type-viewmodel"
import type { CreateUserErrors } from "../../features/user/create-user-error"
import { createUserError, validateCreateUser } from "../../features/user/create-user-error"
import { UserDetailViewModel } from "../../features/user/user-detail-view-model"
import { formatRole, UserRole } from "../../features/user/user-types"
import { useAppState, useViewModel } from "../app/app-hooks"
import { Button } from "../ui-widgets/button/button"

import { ApplicationTypeSelectorTable } from "./application-type-selector-table"

const ROLES = Object.values(UserRole)

interface UserCreateModalProps {
    orgId: number
    orgName: string
    onClose: () => void
    onSuccess: (userId: number) => void
}

export const UserCreateModal = observer(({ orgId, orgName, onClose, onSuccess }: UserCreateModalProps) => {
    const [name, setName] = useState<string>("")
    const [username, setUsername] = useState<string>("")
    const [selectedAppTypeIds, setSelectedAppTypeIds] = useState<number[]>([])
    const [selectedRoles, setSelectedRoles] = useState<UserRole[]>([])
    const [errors, setErrors] = useState<CreateUserErrors>({})
    const [isSubmitting, setIsSubmitting] = useState(false)

    const nameId = useId()
    const usernameId = useId()
    const rolesId = useId()

    const userVM = useViewModel(UserDetailViewModel)
    const appTypeVM = useViewModel(ApplicationTypeViewModel)
    const roles = useAppState().roles

    const toggleRole = useCallback((role: UserRole) => {
        setSelectedRoles((previous) =>
            previous.includes(role) ? previous.filter((existingRole) => existingRole !== role) : [...previous, role]
        )
    }, [])

    const handleSubmit = useCallback(
        async (event: SubmitEvent) => {
            event.preventDefault()

            const {
                name: trimmedName,
                username: trimmedUsername,
                errors: validationErrors
            } = validateCreateUser(name, username)
            setErrors(validationErrors)
            if (Object.keys(validationErrors).length > 0) {
                return
            }

            setIsSubmitting(true)
            try {
                const user = await userVM.createOrganizationUser(
                    trimmedName,
                    trimmedUsername,
                    orgId,
                    selectedRoles,
                    selectedAppTypeIds
                )
                if (!user.success) {
                    // error log done in user service
                    setErrors(createUserError(user.apiError?.status))
                    return
                }
                if (user.data?.id) {
                    onSuccess(user.data.id)
                }
            } finally {
                setIsSubmitting(false)
            }
        },
        [name, onSuccess, orgId, selectedAppTypeIds, selectedRoles, userVM, username]
    )

    useEffect(() => {
        if (orgId) {
            void appTypeVM.getAppTypes(orgId)
        }
    }, [appTypeVM, orgId])

    return (
        <div className="modal block modal-on-background" tabIndex={-1} role="dialog" onClick={onClose}>
            <div
                className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
                role="document"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-content">
                    <div className="modal-header custom-card-header bg-legacy-primary text-white">
                        <h5 className="modal-title">Create User</h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            aria-label="Close"
                            onClick={onClose}
                        />
                    </div>
                    <div className="modal-body py-legacy">
                        <form onSubmit={handleSubmit} className="grid gap-legacy">
                            {orgName && (
                                <div>
                                    Organization:
                                    <span className="font-semibold text-legacy-dark ms-2">{orgName}</span>
                                </div>
                            )}

                            {/* Name */}
                            <div className="form-floating">
                                <input
                                    type="text"
                                    placeholder="Name"
                                    className={clsx("form-control", { "is-invalid": errors.name })}
                                    id={nameId}
                                    value={name}
                                    onChange={(e) => {
                                        setName(e.target.value)
                                        setErrors({})
                                    }}
                                />
                                <label htmlFor={nameId}>Name</label>
                                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                            </div>

                            {/* Username */}
                            <div className="form-floating">
                                <input
                                    type="text"
                                    placeholder="Username (user@company.com)"
                                    className={clsx("form-control", { "is-invalid": errors.username })}
                                    id={usernameId}
                                    value={username}
                                    onChange={(e) => {
                                        setUsername(e.target.value)
                                        setErrors({})
                                    }}
                                />
                                <label htmlFor={usernameId}>Username (user@company.com)</label>
                                {errors.username && <div className="invalid-feedback">{errors.username}</div>}
                            </div>

                            {/* Roles */}
                            <div>
                                <div className="mb-2">Roles</div>
                                <div className="flex flex-col">
                                    {ROLES.map((role) => {
                                        const roleId = `${rolesId}-${role}`
                                        return (
                                            <div key={role} className="form-check mb-1">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id={roleId}
                                                    checked={selectedRoles.includes(role)}
                                                    disabled={!canManageRole(roles, role)}
                                                    onChange={() => toggleRole(role)}
                                                />
                                                <label className="form-check-label" htmlFor={roleId}>
                                                    {formatRole(role)}
                                                </label>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>

                            {appTypeVM.hasAllAppTypes && (
                                <div>
                                    <ApplicationTypeSelectorTable
                                        selectedIds={selectedAppTypeIds}
                                        setSelectedIds={setSelectedAppTypeIds}
                                    />
                                </div>
                            )}

                            {errors.form && <div className="alert alert-danger mb-0">{errors.form}</div>}

                            <div className="modal-footer px-0 pb-0">
                                <Button variant="secondary" onClick={onClose}>
                                    Cancel
                                </Button>
                                <Button type="submit" busy={isSubmitting}>
                                    Create
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
})
